import store from '@state/store'

function continueIfOnboardingEnabled(next) {
  if (!store.getters['user/canAccessOnboarding']) {
    next({ name: 'home' })
    return
  }

  next()
}

const devRoutes = [
  {
    path: '/styleguide',
    name: 'styleGuide',
    component: () => lazyLoadView(import('@views/style-guide/style-guide.vue')),
  },
  {
    path: '/apps-components',
    name: 'appsComponents',
    component: () => lazyLoadView(import('@views/style-guide/apps-components.vue')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        next()
      },
    },
  },
  {
    path: '/formComponents',
    name: 'formComponents',
    component: () => lazyLoadView(import('@views/style-guide/formComponents.vue')),
  },
  {
    path: '/milecountdown',
    name: 'mileCountdown',
    component: () => lazyLoadView(import('@views/style-guide/mileCountdown.vue')),
  },
  {
    path: '/milecountdown2025',
    name: 'mileCountdown2025',
    component: () => lazyLoadView(import('@views/style-guide/mileCountdown2025.vue')),
  },
  {
    path: '/milecountdown2026',
    name: 'mileCountdown2026',
    component: () => lazyLoadView(import('@views/style-guide/mileCountdown2026.vue')),
  },
  {
    path: '/survey-test',
    name: 'surveyTest',
    component: () => lazyLoadView(import('@views/home.vue')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        next()
      },
      sidebarItemName: 'Home',
    },
  },
  {
    path: '/debug',
    name: 'debug-hub',
    component: () => lazyLoadView(import('@views/debug/debug-hub.vue')),
    meta: {
      debugView: true,
      beforeResolve(routeTo, routeFrom, next) {
        next()
      },
    },
  },
  {
    path: '/debug/onboarding',
    name: 'debug-onboarding',
    component: () => lazyLoadView(import('@views/onboarding/debug-onboarding.vue')),
    meta: {
      debugView: true,
      beforeResolve(routeTo, routeFrom, next) {
        next()
      },
    },
  },
  {
    path: '/debug/onboarding/preview',
    name: 'debug-onboarding-preview',
    component: () => lazyLoadView(import('@views/onboarding/debug-onboarding-preview.vue')),
    props: (route) => ({
      debugScenarioId: route.query.scenarioId || 'first-essential-step',
      showWelcomeIntro: route.query.welcome === 'true',
    }),
    meta: {
      debugView: true,
      beforeResolve(routeTo, routeFrom, next) {
        next()
      },
    },
  },
]

const redirectToHomeIfLoggedIn = (next, returnUrl) => {
  if (store.getters['auth/loggedIn']) {
    if (returnUrl) {
      next({ path: returnUrl })
      return
    }

    next({ name: 'home' })
  } else {
    next()
  }
}

const logout = (next) => {
  store.dispatch('auth/logOut').then(() => {
    next()
  })
}

const logoutIfLoggedIn = (next, routeTo) => {
  if (store.getters['auth/loggedIn']) {
    store.dispatch('auth/logOut', routeTo.path).then(() => {
      next()
    })
  } else {
    next()
  }
}

const devLoginRedirect =
  process.env.APP_ENV === 'dev' && process.env.NODE_ENV !== 'production' && !process.env.VUE_E2E_TEST
    ? (route) => ({
        name: 'login-test',
        query: route.query,
      })
    : undefined

const routes = [
  {
    path: '/login',
    name: 'login',
    alias: '/auth',
    component: () => import('@views/login/login_okta.vue'),
    ...(devLoginRedirect ? { redirect: devLoginRedirect } : {}),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        const {
          query: { returnUrl },
        } = routeTo
        redirectToHomeIfLoggedIn(next, returnUrl)
      },
    },
  },
  {
    path: '/login-test',
    name: 'login-test',
    component: () => import('@views/login/login.vue'),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        const {
          query: { returnUrl },
        } = routeTo
        redirectToHomeIfLoggedIn(next, returnUrl)
      },
    },
  },
  {
    path: '/request-password-reset',
    name: 'requestPasswordReset',
    component: () => lazyLoadView(import('@views/login/request-password-reset.vue')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        redirectToHomeIfLoggedIn(next)
      },
    },
  },
  {
    path: '/auth/reset-password',
    name: 'resetPassword',
    component: () => lazyLoadView(import('@views/login/reset-password.vue')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        redirectToHomeIfLoggedIn(next)
      },
    },
  },
  {
    path: '/auth/expired-password',
    name: 'expiredPassword',
    component: () => lazyLoadView(import('@views/login/expired-password.vue')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        redirectToHomeIfLoggedIn(next)
      },
    },
  },
  {
    path: '/auth/register/:oktaId',
    name: 'register',
    component: () => lazyLoadView(import('@views/login/register.vue')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        logoutIfLoggedIn(next, routeTo)
      },
    },
  },
  {
    path: '/auth/reset-account-access/:oktaId',
    name: 'reset-account-access',
    component: () => lazyLoadView(import('@views/login/reset-account-access.vue')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        logoutIfLoggedIn(next, routeTo)
      },
    },
  },
  {
    path: '/auth/reset-security-question',
    name: 'reset-security-question',
    component: () => lazyLoadView(import('@views/login/reset-security-question.vue')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        redirectToHomeIfLoggedIn(next)
      },
    },
  },
  {
    path: '/',
    name: 'home',
    alias: '/home',
    component: () => lazyLoadView(import('@views/home.vue')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        next()
      },
      sidebarItemName: 'Home',
    },
  },
  {
    path: '/onboarding',
    name: 'onboarding',
    component: () => lazyLoadView(import('@views/onboarding/onboarding.vue')),
    props: {
      defaultToDashboard: true,
    },
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        continueIfOnboardingEnabled(next)
      },
    },
  },
  {
    path: '/onboarding/welcome',
    name: 'onboarding-welcome',
    component: () => lazyLoadView(import('@views/onboarding/onboarding.vue')),
    props: {
      showWelcomeIntro: true,
      defaultToDashboard: true,
    },
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        continueIfOnboardingEnabled(next)
      },
    },
  },
  {
    path: '/onboarding-preview',
    name: 'onboarding-preview',
    component: () => lazyLoadView(import('@views/onboarding/onboarding-preview.vue')),
    meta: {
      sidebarItemName: 'Onboarding',
    },
  },
  {
    path: '/apps',
    name: 'apps',
    component: () => lazyLoadView(import('@views/apps.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Apps',
    },
  },
  {
    path: '/event/:eventId',
    name: 'event',
    component: () => lazyLoadView(import('@views/event.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Home',
    },
  },
  {
    path: '/resource/:resourceId',
    name: 'resource',
    alias: ['/resources/post/:resourceId', '/help/knowledge/post/:resourceId'],
    component: () => lazyLoadView(import('@views/resource.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Resources',
    },
  },
  {
    path: '/resource-preview',
    name: 'resource-preview',
    component: () => lazyLoadView(import('@views/resource.vue')),
    meta: {
      sidebarItemName: 'Resources',
    },
  },
  {
    path: '/resources/:category?/:subcategory?',
    name: 'resources',
    component: () => lazyLoadView(import('@views/resources.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Resources',
    },
  },
  {
    path: '/search/:query?',
    name: 'search',
    component: () => lazyLoadView(import('@views/search.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Resources',
    },
  },
  {
    path: '/faq',
    alias: '/help',
    name: 'faq',
    component: () => lazyLoadView(import('@views/help.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'FAQ',
    },
  },
  {
    path: '/draft/mission-against-hunger',
    name: 'missionAgainstHungerDraft',
    component: () => lazyLoadView(import('@views/mission-against-hunger.vue')),
    meta: {
      authRequired: true,
      draft: true,
    },
  },
  {
    path: '/mission-against-hunger',
    name: 'missionAgainstHunger',
    component: () => lazyLoadView(import('@views/mission-against-hunger.vue')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/awards',
    name: 'awards',
    component: () => lazyLoadView(import('@views/awards/awards.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Leaderboards',
      subSidebarItemName: 'awards',
    },
  },
  {
    path: '/education',
    name: 'education',
    component: () => lazyLoadView(import('@views/education/education.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Education',
      subSidebarItemName: 'education',
    },
  },
  {
    path: '/assign-courses',
    name: 'assign-courses',
    component: () => import('@views/education/assign-courses.vue'),
    meta: {
      authRequired: true,
      sidebarItemName: 'Education',
      subSidebarItemName: 'assign-courses',
    },
  },

  {
    path: '/my-courses',
    name: 'my-courses',
    component: () => lazyLoadView(import('@views/education/my-courses.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Education',
      subSidebarItemName: 'my-courses',
    },
  },
  {
    path: '/education-demo',
    name: 'educationDemo',
    component: () => lazyLoadView(import('@views/education/education.vue')),
    meta: {
      authRequired: true,
      demoPage: true,
      sidebarItemName: 'Education Demo',
    },
  },
  {
    path: '/assign-courses-demo',
    name: 'assign-courses-demo',
    component: () => import('@views/education/assign-courses.vue'),
    meta: {
      authRequired: true,
      demoPage: true,
      sidebarItemName: 'Education Demo',
    },
  },
  {
    path: '/online-presence',
    name: 'onlinePresence',
    component: () => lazyLoadView(import('@views/online-presence/online-presence.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Online Presence',
    },
  },
  {
    path: '/debug/online-presence',
    name: 'debugOnlinePresence',
    component: () => lazyLoadView(import('@views/online-presence/debug-online-presence.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Online Presence',
    },
  },
  {
    path: '/awards-iframe',
    name: 'awardsIframe',
    component: () => lazyLoadView(import('@views/awards/awards-iframe.vue')),
  },
  {
    path: '/my-awards',
    name: 'my-awards',
    component: () => lazyLoadView(import('@views/awards/my-awards.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Leaderboards',
      subSidebarItemName: 'my-awards',
    },
  },
  {
    path: '/leaderboards',
    name: 'leaderboards',
    component: () => lazyLoadView(import('@views/leaderboards/leaderboards.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Leaderboards',
      subSidebarItemName: 'leaderboards',
    },
  },
  {
    path: '/my-rankings',
    name: 'myRankings',
    component: () => lazyLoadView(import('@views/leaderboards/my-rankings.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Leaderboards',
      subSidebarItemName: 'myRankings',
    },
  },
  {
    path: '/debug/my-rankings',
    name: 'debugMyRankings',
    component: () => lazyLoadView(import('@views/leaderboards/debug-my-rankings.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Leaderboards',
      subSidebarItemName: 'myRankings',
    },
  },
  {
    path: '/network-connect',
    name: 'network-connect',
    component: () => lazyLoadView(import('@views/network-connect/network-connect.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Network Connect',
    },
  },
  {
    path: '/referral/received/:id',
    name: 'received-referral',
    component: () => lazyLoadView(import('@views/network-connect/received-referral.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Network Connect',
    },
  },
  {
    path: '/referral/office/received/:id',
    name: 'received-office-referral',
    component: () => lazyLoadView(import('@views/network-connect/received-referral.vue')),
    meta: {
      referralType: 'Offices',
      authRequired: true,
      sidebarItemName: 'Network Connect',
    },
  },

  {
    path: '/referral/sent/:id',
    name: 'sent-referral',
    component: () => lazyLoadView(import('@views/network-connect/sent-referral.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Network Connect',
    },
  },
  {
    path: '/create-referral',
    alias: ['/new-referral'],
    name: 'network-connect-new-referral',
    component: () => lazyLoadView(import('@views/network-connect/network-connect-new-referral.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Network Connect',
    },
  },
  {
    path: '/resend-referral/:id',
    name: 'network-connect-resend-referral',
    component: () => lazyLoadView(import('@views/network-connect/network-connect-new-referral.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Network Connect',
    },
  },
  {
    path: '/notificationsManager',
    name: 'notificationsManager',
    component: () => lazyLoadView(import('@views/notificationsManager.vue')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: () => lazyLoadView(import('@views/notifications.vue')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/office-management/user',
    name: 'personnel-management-user-creation',
    component: () => import('@views/personnel/personnel-management-user-form.vue'),
    meta: {
      authRequired: true,
      sidebarItemName: 'Users',
      settingsMenu: true,
      hideMobileUpArrow: true,
    },
  },
  {
    path: '/create-prospect',
    name: 'personnel-management-prospect-creation',
    component: () => import('@views/personnel/personnel-management-user-form.vue'),
    meta: {
      authRequired: true,
      sidebarItemName: 'Users',
      settingsMenu: true,
      hideMobileUpArrow: true,
      hideProspectRider: true,
      createProspect: true,
    },
  },
  {
    path: '/office-management/request-phase-two-access/:id',
    name: 'request-phase-two-access',
    component: () => import('@views/personnel/personnel-management-user-form.vue'),
    meta: {
      authRequired: true,
      sidebarItemName: 'Users',
      requestPhaseTwo: true,
      requestLicensedRole: true,
      settingsMenu: true,
      hideMobileUpArrow: true,
    },
  },
  {
    path: '/office-management/request-loa-licensed/:id',
    name: 'request-loa-licensed',
    component: () => import('@views/personnel/personnel-management-user-form.vue'),
    meta: {
      authRequired: true,
      sidebarItemName: 'Users',
      requestLOALicensed: true,
      requestLicensedRole: true,
      settingsMenu: true,
      hideMobileUpArrow: true,
    },
  },
  {
    path: '/office-management/request-phase-two-access/:id',
    name: 'request-loa-licensed-and-phase-two',
    component: () => import('@views/personnel/personnel-management-user-form.vue'),
    meta: {
      authRequired: true,
      sidebarItemName: 'Users',
      requestPhaseTwo: true,
      requestLOALicensed: true,
      requestLicensedRole: true,
      settingsMenu: true,
      hideMobileUpArrow: true,
    },
  },
  {
    path: '/security',
    name: 'security',
    alias: '/change-password',
    component: () => lazyLoadView(import('@views/settings/security-view.vue')),
    meta: {
      authRequired: true,
      sidebarItemName: 'Settings',
      settingsMenu: true,
    },
  },
  {
    path: '/profile',
    redirect: '/my-info',
  },
  {
    path: '/settings',
    redirect: '/my-info',
  },
  {
    path: '/my-info',
    name: 'myInfo',
    alias: ['/my-tech', '/my-office'],
    component: () => import('@views/settings/my-info.vue'),
    meta: {
      authRequired: true,
      sidebarItemName: 'Office Management',
      settingsMenu: true,
    },
  },
  {
    path: '/debug/my-info',
    name: 'debug-my-info',
    component: () => lazyLoadView(import('@views/personnel/debug-personnel-management.vue')),
    meta: {
      debugView: true,
      authRequired: true,
      sidebarItemName: 'Users',
      settingsMenu: true,
    },
  },

  {
    path: '/debug/education',
    name: 'debug-education',
    component: () => lazyLoadView(import('@views/education/debug-education.vue')),
    meta: {
      debugView: true,
      authRequired: true,
    },
  },

  {
    path: '/debug/assign-courses',
    name: 'debug-assign-courses',
    component: () => lazyLoadView(import('@views/education/debug-assign-courses.vue')),
    meta: {
      debugView: true,
      authRequired: true,
    },
  },
  {
    path: '/user-agreement',
    name: 'userAgreement',
    component: () => lazyLoadView(import('@views/user-agreement.vue')),
  },
  {
    path: '/mobile-terms',
    name: 'mobileTerms',
    component: () => lazyLoadView(import('@views/mobile-terms.vue')),
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      beforeResolve: async (routeTo, routeFrom, next) => {
        logout(next)
      },
    },
  },
  {
    path: '/unsubscribe-email/:id/:type/:hash',
    name: 'unsubscribeEmail',
    component: () => lazyLoadView(import('@views/login/unsubscribe-email.vue')),
  },
  {
    path: '/lo-phase-two-exit-survey/:id/:hash',
    name: 'loPhaseTwoExitSurvey',
    component: () => lazyLoadView(import('@views/login/lo-phase-two-exit-survey.vue')),
  },
  {
    path: '/email-change-confirmation/:id/:hash',
    name: 'emailChangeConfirmation',
    component: () => lazyLoadView(import('@views/login/email-change-confirmation.vue')),
  },
  {
    path: '/creditcardauthorization',
    name: 'creditcardauthorization',
    meta: {
      beforeResolve() {
        window.location.href = 'https://www.emailmeform.com/builder/emf/MottoMortgage/CreditCardAuthorizationForm'
      },
    },
  },
  {
    path: '/recurringcreditcardauthorization',
    name: 'recurringcreditcardauthorization',
    meta: {
      beforeResolve() {
        window.location.href = 'https://www.emailmeform.com/builder/form/1Hczf5a6ZfsDVX67'
      },
    },
  },
  {
    path: '/promnotecreditcardauthorization',
    name: 'promnotecreditcardauthorization',
    meta: {
      beforeResolve() {
        window.location.href = 'https://www.emailmeform.com/builder/form/n3A0MUDw4Xf97'
      },
    },
  },
  {
    path: '/404',
    name: '404',
    component: require('@views/_404.vue').default,
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
    meta: {
      authRequired: true,
    },
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '404',
  },
]

const fallbackRoute = routes.find((route) => route.path === '*')
const appRoutes = routes.filter((route) => route.path !== '*')
const exportRoutes = process.env.APP_ENV !== 'prod' ? [...appRoutes, ...devRoutes, fallbackRoute] : routes

export default exportRoutes
// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    loading: require('@views/_loading.vue').default,
    delay: 400,
    error: require('@views/_timeout.vue').default,
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      return h(AsyncHandler, data, children)
    },
  })
}
